import React, { useEffect, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Restrained dark cinematic image lightbox.
 * - Escape closes
 * - Backdrop click closes
 * - Close button top-right
 * - Locks body scroll while open
 *
 * Two modes:
 *  • Single mode:   pass `src`, `alt`, `caption` (used by Home/Core/Estimate).
 *  • Sequence mode: pass `items=[{src,alt,caption}, ...]` + `index` + `onIndexChange`.
 *    Enables ArrowLeft/ArrowRight keys, on-screen chevrons, dot index, and
 *    horizontal swipe on touch devices. Restrained — no carousel chrome.
 */
export default function ImageLightbox({
  open,
  onClose,
  src,
  alt,
  caption,
  items,
  index = 0,
  onIndexChange,
}) {
  const isSequence = Array.isArray(items) && items.length > 1;
  const current = isSequence ? items[index] : { src, alt, caption };
  const total = isSequence ? items.length : 1;

  const goPrev = useCallback(() => {
    if (!isSequence || !onIndexChange) return;
    onIndexChange((index - 1 + total) % total);
  }, [isSequence, onIndexChange, index, total]);

  const goNext = useCallback(() => {
    if (!isSequence || !onIndexChange) return;
    onIndexChange((index + 1) % total);
  }, [isSequence, onIndexChange, index, total]);

  // Keyboard
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, goPrev, goNext]);

  // Touch swipe
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 50) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  // Preload neighbours for snappy navigation
  const [preloadIdx, setPreloadIdx] = useState(null);
  useEffect(() => {
    if (!open || !isSequence) return;
    const prevI = (index - 1 + total) % total;
    const nextI = (index + 1) % total;
    setPreloadIdx({ prev: prevI, next: nextI });
  }, [open, isSequence, index, total]);

  if (!open || !current?.src) return null;

  return createPortal(
    <div
      data-testid="image-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={current.alt || "Image preview"}
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/92 backdrop-blur-xl px-4 py-12 md:px-10 md:py-16"
      style={{ animation: "lb-fade-in 180ms ease-out" }}
    >
      <style>{`@keyframes lb-fade-in { from { opacity: 0; } to { opacity: 1; } }`}</style>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        data-testid="image-lightbox-close"
        aria-label="Close preview"
        className="absolute top-5 right-5 md:top-7 md:right-7 z-20 w-11 h-11 inline-flex items-center justify-center border border-zinc-700 bg-black/60 text-zinc-200 hover:text-white hover:border-[#ff6b1a] hover:bg-black/80 transition-colors"
      >
        <X size={18} strokeWidth={1.6} />
      </button>

      {isSequence && (
        <>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            data-testid="image-lightbox-prev"
            aria-label="Previous capture"
            className="absolute left-3 md:left-7 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 inline-flex items-center justify-center border border-zinc-700 bg-black/60 text-zinc-200 hover:text-white hover:border-[#ff6b1a] hover:bg-black/80 transition-colors"
          >
            <ChevronLeft size={20} strokeWidth={1.6} />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            data-testid="image-lightbox-next"
            aria-label="Next capture"
            className="absolute right-3 md:right-7 top-1/2 -translate-y-1/2 z-20 w-11 h-11 md:w-12 md:h-12 inline-flex items-center justify-center border border-zinc-700 bg-black/60 text-zinc-200 hover:text-white hover:border-[#ff6b1a] hover:bg-black/80 transition-colors"
          >
            <ChevronRight size={20} strokeWidth={1.6} />
          </button>
        </>
      )}

      <img
        key={current.src}
        src={current.src}
        alt={current.alt || ""}
        decoding="async"
        draggable="false"
        onClick={(e) => e.stopPropagation()}
        data-testid="image-lightbox-img"
        className="max-w-full max-h-full object-contain select-none shadow-[0_40px_120px_rgba(0,0,0,0.7)] border border-[#ff6b1a]/25"
      />

      {/* Hidden preload images for snappy neighbour navigation */}
      {isSequence && preloadIdx && (
        <>
          <link rel="preload" as="image" href={items[preloadIdx.prev]?.src} />
          <link rel="preload" as="image" href={items[preloadIdx.next]?.src} />
        </>
      )}

      {/* Caption + sequence indicator */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-none z-10">
        {current.caption && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-black/70 border border-[#ff6b1a]/40 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 bg-[#ff6b1a] rounded-full animate-pulse" />
            <span
              data-testid="image-lightbox-caption"
              className="font-display uppercase tracking-[0.3em] text-[10px] text-[#ff6b1a] font-bold"
            >
              {current.caption}
            </span>
          </div>
        )}
        {isSequence && (
          <div
            data-testid="image-lightbox-sequence-indicator"
            className="flex items-center gap-3 px-3 py-1.5 bg-black/60 border border-zinc-800 backdrop-blur-sm pointer-events-auto"
          >
            <span className="font-display uppercase tracking-[0.28em] text-[9px] text-zinc-400 font-bold">
              {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
            <span className="flex gap-1.5">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onIndexChange?.(i);
                  }}
                  aria-label={`Go to capture ${i + 1}`}
                  data-testid={`image-lightbox-dot-${i}`}
                  className={`w-1.5 h-1.5 transition-colors ${
                    i === index ? "bg-[#ff6b1a]" : "bg-zinc-700 hover:bg-zinc-500"
                  }`}
                />
              ))}
            </span>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

